import { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { Select, TimePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  LBNodeData,
  IntervalUnit,
  DayOfWeek,
  type DelayWaitEvent,
  LocationTarget,
} from '@/proto/logiblocks/logiblocks';
import { CustomSelectIcon } from '@/assets/icons/icons';
import { DayOfWeekPicker } from '../DayOfWeekPicker';
import { LBNodeUpdateContext } from '../LBNodeUpdateContext';
import styles from '../NodeCustomization.module.css';
import delayStyles from './DelayActionCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { customerLegEnabled, FieldErrorLabel, FieldLabel } from '../shared';
import {
  DelayActionFields,
  LOCATION_TARGET_LABELS,
} from './DelayActionNodeInfo';
import { DEFAULT_DELAY_ACTION, DEFAULT_DELAY_DURATION } from './defaults';
import { getUserTimeZone } from '@/common/utils/utils';
import {
  LogiblocksGate,
  useLogiblocksGateEnabled,
} from '@/pages/Logiblocks/useLogiblocksGateEnabled';
import { DelayUntilEventEditor } from './DelayUntilEventEditor';
import { DelayAddEventScreen } from './DelayAddEventScreen';
import { DelayEventDetail } from './DelayEventDetail';
import { ScreenSlide } from './ScreenSlide';
import {
  createDefaultUntilEvent,
  createEvent,
  newEventId,
  type DelayEventKind,
} from './delayUntilEventModel';
import {
  amountToSeconds,
  secondsToAmount,
} from '@/pages/Logiblocks/intervalUnits';
import {
  formatTimezoneOffset,
  userSecondsToUtcSeconds,
  utcSecondsToUserSeconds,
} from '@/pages/Logiblocks/dateTimeUtils';

enum DelayType {
  DURATION = 'duration',
  UNTIL_TIME = 'until_time',
  UNTIL_DAY = 'until_day',
  UNTIL_EVENT = 'until_event',
  UNTIL_LOCATION_OPEN = 'until_location_open',
}

// Pickup and drop offices come from the active execution leg (customer/execution leg
// companies only).
const ACTIVE_LEG_TARGETS = new Set([
  LocationTarget.PICKUP_OFFICE,
  LocationTarget.DROP_OFFICE,
]);
const LOCATION_TARGET_ORDER = [
  LocationTarget.ORIGIN,
  LocationTarget.DESTINATION,
  LocationTarget.CONTROLLING_OFFICE,
  LocationTarget.PICKUP_OFFICE,
  LocationTarget.DROP_OFFICE,
];

// Seconds since midnight to dayjs (for TimePicker)
const secondsToDayjs = (seconds: number): Dayjs =>
  dayjs().startOf('day').add(seconds, 'seconds');

// Dayjs to seconds since midnight (from TimePicker)
const dayjsToSeconds = (time: Dayjs): number =>
  time.hour() * 3600 + time.minute() * 60;

// Get default until_time (current time rounded to nearest 15 min, in UTC seconds since midnight)
const getDefaultUntilTimeUtc = (): number => {
  const now = new Date();
  const minutes = Math.round(now.getMinutes() / 15) * 15;
  now.setMinutes(minutes, 0, 0);
  return now.getUTCHours() * 3600 + now.getUTCMinutes() * 60;
};

// Determine delay type based on which field is set
const getDelayType = (data: LBNodeData['delay_action']): DelayType => {
  if (data?.until_event !== undefined) {
    return DelayType.UNTIL_EVENT;
  }
  if (data?.until_location_open !== undefined) {
    return DelayType.UNTIL_LOCATION_OPEN;
  }
  if (data?.until_day !== undefined) {
    return DelayType.UNTIL_DAY;
  }
  if (data?.until_time !== undefined) {
    return DelayType.UNTIL_TIME;
  }
  return DelayType.DURATION;
};

const DELAY_TYPE_OPTIONS = [
  { value: DelayType.DURATION, label: 'Delay for' },
  { value: DelayType.UNTIL_TIME, label: 'Delay until' },
  { value: DelayType.UNTIL_DAY, label: 'Delay until day' },
  { value: DelayType.UNTIL_LOCATION_OPEN, label: 'Delay until location opens' },
];

type EventView =
  | { screen: 'form' }
  | { screen: 'menu' }
  | {
      screen: 'settings';
      event: DelayWaitEvent;
      /** Where the settings were opened from: the Add event screen or a card. */
      from: 'menu' | 'card';
    };

/** How many steps a screen is from the Delay form, which is what tells going on from going back. */
function screenDepth(view: EventView): number {
  if (view.screen === 'form') return 0;
  if (view.screen === 'menu') return 1;
  return view.from === 'menu' ? 2 : 1;
}

const UNTIL_EVENT_OPTION = {
  value: DelayType.UNTIL_EVENT,
  label: 'Delay until event',
};

const UNIT_OPTIONS = [
  { value: IntervalUnit.SECONDS, label: 'Seconds' },
  { value: IntervalUnit.MINUTES, label: 'Minutes' },
  { value: IntervalUnit.HOURS, label: 'Hours' },
];

/**
 * Customization UI for DELAY_ACTION nodes.
 * Allows configuring a delay duration with unit selection (minutes/hours),
 * or a specific time of day to delay until.
 */
export const DelayActionCustomization: CustomizationComponent = ({
  nodeId,
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
  isSidePannelOpen,
  setPanelScreen,
}) => {
  // Read without requiring the provider: only "Notify automation owner" needs it.
  const ownerEmail = useContext(LBNodeUpdateContext)?.ownerEmail;
  const daysOfWeekError = validationErrors?.find(
    (e) => e.field === DelayActionFields.DAYS_OF_WEEK
  );
  const delayDurationError = validationErrors?.find(
    (e) => e.field === DelayActionFields.DELAY_DURATION
  );
  const timeError = validationErrors?.find(
    (e) => e.field === DelayActionFields.TIME
  );
  const currentDelayData = nodeData.delay_action || DEFAULT_DELAY_ACTION;
  const isUntilEventEnabled = useLogiblocksGateEnabled(
    LogiblocksGate.DELAY_UNTIL_EVENT
  );
  // Which panel screen is open over the form: the Add event screen, or one
  // event's settings. A screen takes the whole panel, so the form is not drawn
  // while one is open.
  const [eventView, setEventView] = useState<EventView>({ screen: 'form' });
  // A screen belongs to the panel session that opened it.
  useEffect(() => {
    if (isSidePannelOpen === false) setEventView({ screen: 'form' });
  }, [isSidePannelOpen]);
  // The last "Delay until event" setup seen, so switching away and back keeps it.
  const lastUntilEventRef = useRef(currentDelayData.until_event);
  if (currentDelayData.until_event) {
    lastUntilEventRef.current = currentDelayData.until_event;
  }

  const [delayType, setDelayType] = useState(() =>
    getDelayType(currentDelayData)
  );
  // Fall back to DEFAULT_DELAY_DURATION when the node is in UNTIL_TIME / UNTIL_DAY mode
  // (no `duration` field). (Without this, `delayAmount` starts as NaN and crashes proto
  // encoding.)
  const [delayAmount, setDelayAmount] = useState(
    secondsToAmount(
      currentDelayData.duration?.seconds ?? DEFAULT_DELAY_DURATION.seconds,
      currentDelayData.duration?.display_unit ??
        DEFAULT_DELAY_DURATION.display_unit
    )
  );
  const [unit, setUnit] = useState(
    currentDelayData.duration?.display_unit ??
      DEFAULT_DELAY_DURATION.display_unit
  );
  // Time-of-day fields keep the user's cleared state as `undefined` so validation can flag it.
  // Seed a default only when the field doesn't exist yet (new node / mode-switch).
  const [untilTimeUtc, setUntilTimeUtc] = useState<number | undefined>(() =>
    currentDelayData.until_time
      ? currentDelayData.until_time.seconds_since_midnight
      : getDefaultUntilTimeUtc()
  );
  const [untilDays, setUntilDays] = useState<DayOfWeek[]>(
    currentDelayData.until_day?.days_of_week ?? [DayOfWeek.MONDAY]
  );
  const [untilDayTimeUtc, setUntilDayTimeUtc] = useState<number | undefined>(
    () =>
      currentDelayData.until_day
        ? currentDelayData.until_day.seconds_since_midnight
        : getDefaultUntilTimeUtc()
  );

  const [locationTarget, setLocationTarget] = useState<LocationTarget>(
    currentDelayData.until_location_open?.target ??
      LocationTarget.LOCATION_TARGET_UNSPECIFIED
  );
  const locationError = validationErrors?.find(
    (e) => e.field === DelayActionFields.LOCATION
  );
  const locationOptions = useMemo(() => {
    const customerLeg = customerLegEnabled();
    return LOCATION_TARGET_ORDER.filter(
      (target) =>
        customerLeg ||
        !ACTIVE_LEG_TARGETS.has(target) ||
        // Keep a saved choice visible even if the company setting changed.
        target === locationTarget
    ).map((target) => ({
      value: target,
      label: LOCATION_TARGET_LABELS[target],
    }));
  }, [locationTarget]);

  // Memoize timezone info to avoid recalculating on every render
  const timezoneInfo = useMemo(() => {
    const tz = getUserTimeZone();
    return {
      name: tz.name,
      displayString: `Timezone set to (${formatTimezoneOffset(tz.offsetMinutes)}) ${tz.name}`,
    };
  }, []);

  // Update local state when nodeData changes (e.g., node switching)
  useEffect(() => {
    const delayData = nodeData.delay_action || DEFAULT_DELAY_ACTION;
    const newDelayType = getDelayType(delayData);
    setDelayType(newDelayType);

    switch (newDelayType) {
      case DelayType.UNTIL_LOCATION_OPEN:
        setLocationTarget(
          delayData.until_location_open?.target ??
            LocationTarget.LOCATION_TARGET_UNSPECIFIED
        );
        break;
      case DelayType.UNTIL_DAY:
        setUntilDays(delayData.until_day?.days_of_week ?? [DayOfWeek.MONDAY]);
        setUntilDayTimeUtc(
          delayData.until_day
            ? delayData.until_day.seconds_since_midnight
            : getDefaultUntilTimeUtc()
        );
        break;
      case DelayType.UNTIL_TIME:
        setUntilTimeUtc(
          delayData.until_time
            ? delayData.until_time.seconds_since_midnight
            : getDefaultUntilTimeUtc()
        );
        break;
      case DelayType.UNTIL_EVENT:
        // The event editor reads its data straight from nodeData.
        break;
      case DelayType.DURATION:
      default: {
        const newSeconds =
          delayData.duration?.seconds ?? DEFAULT_DELAY_DURATION.seconds;
        const newUnit =
          delayData.duration?.display_unit ??
          DEFAULT_DELAY_DURATION.display_unit;
        setUnit(newUnit);
        setDelayAmount(secondsToAmount(newSeconds, newUnit));
        break;
      }
    }
  }, [nodeData]);

  /**
   * Handles changes to the duration amount input.
   * Updates local state and propagates the change to the parent via onChange.
   */
  const handleAmountChange = (newAmount: number) => {
    setDelayAmount(newAmount);

    const updatedData: Partial<LBNodeData> = {
      delay_action: {
        duration: {
          seconds: amountToSeconds(newAmount, unit),
          display_unit: unit,
        },
      },
    };

    onChange(updatedData);
  };

  /**
   * Handles changes to the duration unit dropdown.
   * Updates local state and propagates the change to the parent via onChange.
   */
  const handleUnitChange = (newUnit: IntervalUnit) => {
    setUnit(newUnit);

    const updatedData: Partial<LBNodeData> = {
      delay_action: {
        duration: {
          seconds: amountToSeconds(delayAmount, newUnit),
          display_unit: newUnit,
        },
      },
    };

    onChange(updatedData);
  };

  /**
   * Handles switching between delay type modes.
   */
  const handleDelayTypeChange = (newType: DelayType) => {
    setDelayType(newType);

    let updatedData: Partial<LBNodeData>;
    switch (newType) {
      case DelayType.UNTIL_EVENT:
        updatedData = {
          delay_action: {
            until_event: lastUntilEventRef.current ?? createDefaultUntilEvent(),
          },
        };
        break;
      case DelayType.UNTIL_LOCATION_OPEN:
        updatedData = {
          delay_action: { until_location_open: { target: locationTarget } },
        };
        break;
      case DelayType.UNTIL_DAY:
        updatedData = {
          delay_action: {
            until_day: {
              days_of_week: untilDays,
              seconds_since_midnight: untilDayTimeUtc,
            },
          },
        };
        break;
      case DelayType.UNTIL_TIME:
        updatedData = {
          delay_action: {
            until_time: {
              seconds_since_midnight: untilTimeUtc,
            },
          },
        };
        break;
      case DelayType.DURATION:
      default:
        updatedData = {
          delay_action: {
            duration: {
              seconds: amountToSeconds(delayAmount, unit),
              display_unit: unit,
            },
          },
        };
        break;
    }
    onChange(updatedData);
  };

  /**
   * Handles time picker changes for "Delay until" mode.
   * A null value means the user cleared the picker — propagate as undefined so validation flags it.
   */
  const handleUntilTimeChange = (timeValue: Dayjs | null) => {
    const utcSeconds = timeValue
      ? userSecondsToUtcSeconds(dayjsToSeconds(timeValue), timezoneInfo.name)
      : undefined;
    setUntilTimeUtc(utcSeconds);

    onChange({
      delay_action: {
        until_time: {
          seconds_since_midnight: utcSeconds,
        },
      },
    });
  };

  /**
   * Handles day selection changes from DayOfWeekPicker in "Delay until day" mode.
   * Updates local state and propagates the change to the parent via onChange.
   */
  const handleDaysChange = (newDays: DayOfWeek[]) => {
    setUntilDays(newDays);

    const updatedData: Partial<LBNodeData> = {
      delay_action: {
        until_day: {
          days_of_week: newDays,
          seconds_since_midnight: untilDayTimeUtc,
        },
      },
    };

    onChange(updatedData);
  };

  /**
   * Handles time picker changes for "Delay until day" mode.
   * A null value means the user cleared the picker — propagate as undefined so validation flags it.
   */
  const handleUntilDayTimeChange = (timeValue: Dayjs | null) => {
    const utcSeconds = timeValue
      ? userSecondsToUtcSeconds(dayjsToSeconds(timeValue), timezoneInfo.name)
      : undefined;
    setUntilDayTimeUtc(utcSeconds);

    onChange({
      delay_action: {
        until_day: {
          days_of_week: untilDays,
          seconds_since_midnight: utcSeconds,
        },
      },
    });
  };

  const handleLocationTargetChange = (target: LocationTarget) => {
    setLocationTarget(target);
    onChange({ delay_action: { until_location_open: { target } } });
  };

  // Convert UTC seconds to a Dayjs in the user's timezone for the TimePicker display.
  // null when the user has cleared the time so the picker shows its placeholder.
  const untilTimeDayjs =
    untilTimeUtc === undefined
      ? null
      : secondsToDayjs(
          utcSecondsToUserSeconds(untilTimeUtc, timezoneInfo.name)
        );
  const untilDayTimeDayjs =
    untilDayTimeUtc === undefined
      ? null
      : secondsToDayjs(
          utcSecondsToUserSeconds(untilDayTimeUtc, timezoneInfo.name)
        );

  // The new mode is offered only behind its Statsig gate; a node that already
  // uses it keeps the option so its label shows and nothing looks lost.
  const delayTypeOptions =
    isUntilEventEnabled || delayType === DelayType.UNTIL_EVENT
      ? [...DELAY_TYPE_OPTIONS, UNTIL_EVENT_OPTION]
      : DELAY_TYPE_OPTIONS;

  const untilEvent = currentDelayData.until_event;
  const showsEventScreen =
    delayType === DelayType.UNTIL_EVENT &&
    untilEvent !== undefined &&
    eventView.screen !== 'form';

  const depth = showsEventScreen ? screenDepth(eventView) : 0;

  if (showsEventScreen && eventView.screen === 'menu') {
    return (
      <ScreenSlide depth={depth}>
        <DelayAddEventScreen
          setPanelScreen={setPanelScreen}
          onBack={() => setEventView({ screen: 'form' })}
          onPick={(kind: DelayEventKind) =>
            setEventView({
              screen: 'settings',
              event: createEvent(kind, newEventId()),
              from: 'menu',
            })
          }
        />
      </ScreenSlide>
    );
  }

  if (showsEventScreen && eventView.screen === 'settings') {
    const { event, from } = eventView;
    return (
      <ScreenSlide depth={depth}>
        <DelayEventDetail
          // A different event gets a fresh draft.
          key={event.id}
          nodeId={nodeId}
          event={event}
          isNew={from === 'menu'}
          isReadOnly={isReadOnly}
          setPanelScreen={setPanelScreen}
          onCancel={() =>
            setEventView(
              from === 'menu' ? { screen: 'menu' } : { screen: 'form' }
            )
          }
          onConfirm={(confirmed) => {
            const events = untilEvent.events;
            const exists = events.some((e) => e.id === confirmed.id);
            onChange({
              delay_action: {
                until_event: {
                  ...untilEvent,
                  events: exists
                    ? events.map((e) => (e.id === confirmed.id ? confirmed : e))
                    : [...events, confirmed],
                },
              },
            });
            setEventView({ screen: 'form' });
          }}
        />
      </ScreenSlide>
    );
  }

  return (
    <ScreenSlide depth={depth}>
      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Type</label>
        <Select
          value={delayType}
          onChange={handleDelayTypeChange}
          disabled={isReadOnly}
          style={{ width: '100%' }}
          suffixIcon={<CustomSelectIcon />}
          className='agentSelect agentSelect--darkText'
        >
          {delayTypeOptions.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      </div>

      {delayType === DelayType.DURATION && (
        <div className={delayStyles.durationRow}>
          <div
            className={`${styles.customizationSection} ${delayStyles.durationRowField}`}
          >
            <FieldLabel required className={styles.sectionLabel}>
              Amount
            </FieldLabel>
            <input
              type='number'
              min={0.1}
              step={0.1}
              value={delayAmount}
              onChange={(e) =>
                handleAmountChange(parseFloat(e.target.value) || 1)
              }
              className={styles.lbNumberInput}
              disabled={isReadOnly}
            />
            <FieldErrorLabel error={delayDurationError} />
          </div>

          <div
            className={`${styles.customizationSection} ${delayStyles.durationRowField}`}
          >
            <label className={styles.sectionLabel}>Unit</label>
            <Select
              value={unit}
              onChange={handleUnitChange}
              disabled={isReadOnly}
              style={{ width: '100%' }}
              suffixIcon={<CustomSelectIcon />}
              className='agentSelect agentSelect--darkText'
            >
              {UNIT_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {delayType === DelayType.UNTIL_TIME && (
        <div className={styles.customizationSection}>
          <FieldLabel required className={styles.sectionLabel}>
            At
          </FieldLabel>
          <TimePicker
            value={untilTimeDayjs}
            onChange={handleUntilTimeChange}
            format='h:mm A'
            use12Hours
            disabled={isReadOnly}
            className={styles.lbTimePicker}
          />
          <FieldErrorLabel error={timeError} />
          <span className={delayStyles.timezoneNote}>
            {timezoneInfo.displayString}
          </span>
        </div>
      )}

      {delayType === DelayType.UNTIL_EVENT && currentDelayData.until_event && (
        <DelayUntilEventEditor
          nodeId={nodeId}
          untilEvent={currentDelayData.until_event}
          onChange={(untilEvent) =>
            onChange({ delay_action: { until_event: untilEvent } })
          }
          isReadOnly={isReadOnly}
          validationErrors={validationErrors}
          ownerEmail={ownerEmail}
          onAddEvent={() => setEventView({ screen: 'menu' })}
          onOpenEvent={(event) =>
            setEventView({ screen: 'settings', event, from: 'card' })
          }
        />
      )}

      {delayType === DelayType.UNTIL_DAY && (
        <>
          <div className={styles.customizationSection}>
            <FieldLabel required className={styles.sectionLabel}>
              Days
            </FieldLabel>
            <DayOfWeekPicker
              selectedDays={untilDays}
              onChange={handleDaysChange}
              disabled={isReadOnly}
            />
            <FieldErrorLabel error={daysOfWeekError} />
          </div>

          <div className={styles.customizationSection}>
            <FieldLabel required className={styles.sectionLabel}>
              At
            </FieldLabel>
            <TimePicker
              value={untilDayTimeDayjs}
              onChange={handleUntilDayTimeChange}
              format='h:mm A'
              use12Hours
              disabled={isReadOnly}
              className={styles.lbTimePicker}
            />
            <FieldErrorLabel error={timeError} />
            <span className={delayStyles.timezoneNote}>
              {timezoneInfo.displayString}
            </span>
          </div>
        </>
      )}

      {delayType === DelayType.UNTIL_LOCATION_OPEN && (
        <div className={styles.customizationSection}>
          <FieldLabel required className={styles.sectionLabel}>
            {DelayActionFields.LOCATION}
          </FieldLabel>
          <Select
            value={
              locationTarget === LocationTarget.LOCATION_TARGET_UNSPECIFIED
                ? undefined
                : locationTarget
            }
            placeholder='Select a location'
            onChange={handleLocationTargetChange}
            disabled={isReadOnly}
            style={{ width: '100%' }}
            suffixIcon={<CustomSelectIcon />}
            className='agentSelect agentSelect--darkText'
            options={locationOptions}
          />
          <FieldErrorLabel error={locationError} />
          <span className={styles.timezoneNote}>
            Waits for the location&apos;s next opening, by its operating hours
            and time zone. Continues at once if it is open or has no hours set.
          </span>
        </div>
      )}
    </ScreenSlide>
  );
};
